import React from "react";
import { useAffAuth } from "../store";
import { Users2, DollarSign, Receipt, Wallet } from "lucide-react";
import { useT } from "../i18n";

function fmtUsd(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

function KpiCard({ label, value, icon: Icon, accent = "default", testid }) {
  const cls =
    accent === "green"
      ? "border-emerald-500/40"
      : accent === "yellow"
      ? "border-amber-500/40"
      : "border-border";
  return (
    <div
      data-testid={testid}
      className={`border ${cls} bg-secondary/20 px-4 py-4 flex items-start gap-3`}
    >
      <Icon className="w-4 h-4 mt-1 text-muted-foreground" />
      <div className="flex-1">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
          {label}
        </div>
        <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
      </div>
    </div>
  );
}

export default function AffDashboard() {
  const { partner } = useAffAuth();
  const t = useT();
  const k = partner?.kpis || {};
  const pct = partner?.commission_pct ?? 20;
  const currency = partner?.display_currency || "USD";
  const threshold = (partner?.min_payout_usd ?? 25).toFixed(0);

  return (
    <div className="space-y-5" data-testid="aff-dashboard">
      <header>
        <h1 className="text-2xl font-medium tracking-tight">
          {t("dash.welcome", { name: partner?.name || "Partner" })}
        </h1>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">
          {t("dash.subtitle", { pct, currency })}
        </p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label={t("dash.kpi.signups")}
          value={k.registrations ?? 0}
          icon={Users2}
          testid="aff-kpi-signups"
        />
        <KpiCard
          label={t("dash.kpi.active")}
          value={k.active_payers ?? 0}
          icon={Receipt}
          testid="aff-kpi-active"
        />
        <KpiCard
          label={t("dash.kpi.earned")}
          value={fmtUsd(k.earned_usd)}
          icon={DollarSign}
          testid="aff-kpi-earned"
        />
        <KpiCard
          label={t("dash.kpi.pending")}
          value={fmtUsd(k.pending_usd)}
          icon={Wallet}
          accent="green"
          testid="aff-kpi-pending"
        />
      </div>

      <div className="border border-border p-5 bg-secondary/10 space-y-3">
        <h2 className="text-sm font-mono uppercase tracking-[0.18em] text-muted-foreground">
          {t("dash.how.title")}
        </h2>
        <ul className="text-sm space-y-2">
          <li>
            <span className="text-emerald-400">①</span> {t("dash.how.step1")}{" "}
            <strong>{t("dash.how.step1_link")}</strong>
            {t("dash.how.step1_dot")}
          </li>
          <li>
            <span className="text-emerald-400">②</span> {t("dash.how.step2_prefix")}{" "}
            <code className="font-mono text-xs">
              https://t.me/Clipra_bot?start=aff_&lt;CODE&gt;
            </code>{" "}
            {t("dash.how.step2_suffix")}
          </li>
          <li>
            <span className="text-emerald-400">③</span> {t("dash.how.step3", { pct })}
          </li>
          <li>
            <span className="text-emerald-400">④</span> {t("dash.how.step4", { threshold })}
          </li>
        </ul>
      </div>

      {k.requested_payout && (
        <div className="border border-amber-500/40 bg-amber-500/[0.05] px-4 py-3 text-sm">
          <strong>{t("dash.payout_requested")}</strong>{" "}
          <span className="text-muted-foreground">
            {t("dash.payout_requested_hint")}
          </span>
        </div>
      )}
    </div>
  );
}
